import type { ManipulateType } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/sk.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'
import type { Timestamp } from 'firebase/firestore'
import { orderBy, range } from 'lodash-es'
import type { Collections } from './constants/firebase.ts'
import { defaultTimezone } from './constants/timezone.js'
import type { Activity } from './types/Activity.ts'
import type { Agreement, AgreementType } from './types/Agreement.ts'
import type { Message } from './types/Chat.ts'
import { FeedbackAction, NpsBand } from './types/Feedback.js'
import { getNpsBand } from './feedback/index.js'
import type { Timestamp as FirebaseTimestamp } from './types/firebase.ts'
import type { OrgId } from './types/ids.ts'
import type { Job } from './types/Job.ts'
import type { NotificationType } from './types/Notifications.ts'
import { ORG_GOV_TYPE, OrgOpportunitiesType } from './types/Org.js'
import type { ConsentsData, Org } from './types/Org.ts'
import type { Project } from './types/Project.ts'
import type { Skill } from './types/Skill.ts'
import type { JobApplicationFormDraft, UserProfile } from './types/User.ts'
import { VisibilityChangeAction, VisibilityType } from './types/visibility.js'

dayjs.extend(utc)
dayjs.extend(timezone)

dayjs.tz.setDefault(defaultTimezone)

dayjs.extend(relativeTime)
dayjs.locale('sk')

export const DATABASE_DATE_FORMAT = 'YYYY-MM-DD'
export const DEFAULT_DATE_FORMAT = 'D. M. YYYY'
export const DEFAULT_TIME_FORMAT = 'H:mm'
export const DEFAULT_TIME_ZONE = 'Europe/Bratislava'

export const URL_REGEXP_ANY =
    /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi

export const EMAIL_REGEXP_ANY =
    /(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/

export const HTML_TAGS_REGEXP = /(<([^>]+)>)/gi

export function clamp(min: number, num: number, max: number): number {
    return Math.min(Math.max(num, min), max)
}

export function removeHTMLTags(value: string): string {
    return value.replace(HTML_TAGS_REGEXP, '')
}

export async function asyncForEach<T>(array: T[], callback: (item: T, index: number, array: T[]) => Promise<void>) {
    for (const [index, item] of array.entries()) {
        await callback(item, index, array)
    }
}

export function listYearsBack(until: number | undefined = undefined, max: number = 100) {
    const currentYear = new Date().getFullYear()

    if (!until) {
        until = currentYear - max
    }

    return range(currentYear, until - 1)
}

export function readFileAsDataUrl(file: File) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e?.target?.result)
        reader.onerror = (e) => reject(e)
        reader.readAsDataURL(file)
    })
}

export function isValidDate(date: any): boolean {
    return !isNaN(date?.getTime?.())
}

export function parseDate(date: any): Date {
    if (date instanceof Date) {
        return date
    }

    if (typeof date === 'number') {
        return new Date(date)
    }

    if (date?.seconds) {
        return new Date(date.seconds * 1000)
    }

    if (date?._seconds) {
        return new Date(date._seconds * 1000)
    }

    return dayjs(date).toDate()
}

export const addDays = (date: any, days: number): Date => {
    const parsedDate = dayjs(parseDate(date))
    return parsedDate.add(days, 'day').toDate()
}

export function isToday(date: string): boolean {
    return dayjs().isSame(date, 'day')
}

export function isThisWeek(date: string): boolean {
    return dayjs().isSame(date, 'week')
}

export function daysTo(to: Date | Timestamp) {
    return daysBetween(new Date(), to)
}

export function daysBetween(from: Date | Timestamp, to: Date | Timestamp) {
    const fromDate = parseDate(from).setHours(12, 0, 0, 0)
    const toDate = parseDate(to).setHours(12, 0, 0, 0)

    return dayjs(toDate).diff(dayjs(fromDate), 'days')
}

export function secondsBetween(from: Date | Timestamp, to: Date | Timestamp) {
    return dayjs(parseDate(to)).diff(dayjs(parseDate(from)), 'seconds')
}

export function dateAfter(from: Date | Timestamp, amount: number, unit: ManipulateType) {
    return dayjs(parseDate(from)).add(amount, unit).toDate()
}

export function isPast(date: Date | Timestamp) {
    return dayjs(parseDate(date)).isBefore(dayjs())
}

export function computeAge(birthday: any): number | undefined {
    birthday = parseDate(birthday)

    if (!isValidDate(birthday)) {
        return undefined
    }

    return dayjs().diff(birthday, 'year')
}

export function getFullName(user: { firstName?: string; lastName?: string } = {}) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ')
}

// Value of `salaryUnit` that represents a monthly EUR figure (see salaryUnits in education.ts).
export const MONTHLY_SALARY_UNIT = 'EUR / mesiac'

// Render a monthly compensation figure for HR-facing UI. Returns '' for empty / zero
// so callers can `v-if` it away (we never show 0 or null).
export function formatMonthlySalary(salary?: number | null): string {
    return typeof salary === 'number' && salary > 0 ? `${salary.toLocaleString('sk-SK')} € / mes.` : ''
}

// Advertised salary range of a job, but only when it is expressed monthly — so it can be
// compared with / suggested for the (always monthly) proposed candidate salary. Returns null
// when the job is paid hourly/daily or has no usable range. Missing unit is treated as monthly
// (the app default), matching how new jobs are created.
export function getMonthlySalaryRange(
    job: Pick<Job, 'salaryMin' | 'salaryMax' | 'salaryUnit'>
): { min?: number; max?: number } | null {
    if ((job.salaryUnit || MONTHLY_SALARY_UNIT) !== MONTHLY_SALARY_UNIT) {
        return null
    }

    const min = typeof job.salaryMin === 'number' && job.salaryMin > 0 ? job.salaryMin : undefined
    const max = typeof job.salaryMax === 'number' && job.salaryMax > 0 ? job.salaryMax : undefined

    if (min === undefined && max === undefined) {
        return null
    }

    return { min, max }
}

type KeysMatching<T extends object, V> = {
    [K in keyof T]: T[K] extends V ? K : never
}[keyof T]

export function formatObjectDate<
    TObject extends object,
    TParam extends KeysMatching<TObject, FirebaseTimestamp | undefined>,
    TLang extends KeysMatching<TObject, string | ('sk' | 'en') | undefined>
>(obj: TObject, objKey: TParam, langKey: TLang = 'lang' as TLang): string {
    if (!obj[objKey]) return ''

    const lang = obj[langKey] || 'sk'

    return formatDate(obj[objKey], {
        format: lang === 'sk' ? 'D. M. YYYY' : 'D MMMM YYYY',
        locale: lang === 'sk' ? 'sk' : 'en'
    })
}

export function formatDate(
    value: any,
    {
        format = DEFAULT_DATE_FORMAT,
        timezone = DEFAULT_TIME_ZONE,
        locale = 'sk'
    }: { format?: string; locale?: 'sk' | 'en'; timezone?: string } = {}
): string {
    if (!value) {
        return ''
    }

    return dayjs(parseDate(value)).tz(timezone).locale(locale!).format(format)
}

export function formatDateTime(value: any) {
    return formatDate(value, {
        format: `${DEFAULT_DATE_FORMAT}・${DEFAULT_TIME_FORMAT}`
    })
}

export function formatTimeAgo(date: any, lessYearFormat = 'D. MMM', divider = ' o') {
    date = parseDate(date)

    const hours = dayjs().diff(date, 'hour')

    if (hours < 6) {
        return dayjs(date).fromNow()
    }

    /** *
     * We have to use format('YYYY-MM-DD'). We have to compare only days
     * to know, if it was today or yesterday ...
     **/
    const days = dayjs().diff(dayjs(date).format('YYYY-MM-DD'), 'd', true)

    if (days < 1) {
        return `dnes${divider} ${dayjs(date).format(DEFAULT_TIME_FORMAT)}`
    }

    if (days < 2) {
        return `včera${divider} ${dayjs(date).format(DEFAULT_TIME_FORMAT)}`
    }

    if (days < 330) {
        return dayjs(date).format(lessYearFormat)
    }

    return dayjs(date).format(DEFAULT_DATE_FORMAT)
}

export const localizedTimeAgo = (date: any, locale: 'sk' | 'en' = 'sk') => {
    const parsedDate = parseDate(date)

    return dayjs(parsedDate).locale(locale).fromNow()
}

export function capitalize(value: string) {
    value = (value || '').toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
}

export function getLength(value?: any) {
    return value?.length || 0
}

export function orderByDate<T>(
    collection: T[],
    { direction = 'desc', dateAttribute = 'createdAt' }: { direction?: 'desc' | 'asc'; dateAttribute?: string } = {}
): T[] {
    return orderBy(collection, (document: any) => parseDate(document[dateAttribute])?.getTime(), direction)
}

export const getTime = (date: Timestamp | Date) => {
    return parseDate(date).getTime()
}

export const sortByCreatedAtDesc = (a: Message, b: Message) => {
    return getTime(b.createdAt) - getTime(a.createdAt)
}

type ScrollElementLike = {
    querySelector: (selector: string) => ScrollElementLike | null
    focus?: () => void
    getBoundingClientRect: () => { left: number; top: number }
}

type BrowserDomLike = {
    document: { querySelector: (selector: string) => ScrollElementLike | null }
    window: {
        scrollTo: (options: { left: number; top: number; behavior: 'smooth' }) => void
        scrollX: number
        scrollY: number
    }
}

function getBrowserDom(): BrowserDomLike | null {
    const browser = globalThis as {
        document?: BrowserDomLike['document']
        window?: BrowserDomLike['window']
    }

    if (!browser.document || !browser.window) {
        return null
    }

    return {
        document: browser.document,
        window: browser.window
    }
}

export function scrollToError(errorClassName: string = 'CFormGroup--invalid') {
    const browser = getBrowserDom()
    if (!browser) {
        return
    }

    const errorElement = browser.document.querySelector(`.${errorClassName}`)

    if (!errorElement) {
        return
    }
    const inputElement = errorElement.querySelector('input, textarea') ?? errorElement

    inputElement.focus?.()

    const { left, top } = getOffset(errorElement, browser.window)

    browser.window.scrollTo({
        left,
        top: top - 150,
        behavior: 'smooth'
    })
}

function getOffset(
    el: { getBoundingClientRect: () => { left: number; top: number } },
    windowObject: { scrollX: number; scrollY: number }
) {
    const rect = el.getBoundingClientRect()

    return {
        left: rect.left + windowObject.scrollX,
        top: rect.top + windowObject.scrollY
    }
}

export function addArrayItem<T = unknown>(array: T[] | undefined, item: T, index?: number): T[] {
    const value = [...(array || [])]

    if (index === undefined) {
        value.push(item)
    } else {
        value.splice(index, 0, item)
    }

    return value
}

export function removeArrayItem<T = unknown>(array: T[] | undefined, index: number): T[] {
    const value = [...(array || [])]

    value.splice(index, 1)

    return value
}

export function updateArrayItem<T = unknown>(array: T[] | undefined, index: number, item: T | ((value: T) => T)): T[] {
    const value = [...(array || [])]
    if (index < 0 || index >= value.length) {
        return value
    }

    if (typeof item === 'function') {
        item = (item as (value: T) => T)(value[index] as T)
    }

    value.splice(index, 1, item as T)

    return value
}

export function swapArrayItems<T = unknown>(array: T[] | undefined, index1: number, index2: number): T[] {
    const value = [...(array || [])]

    if (index1 < 0 || index2 < 0 || index1 >= value.length || index2 >= value.length) {
        return value
    }

    const item1 = value[index1] as T
    const item2 = value[index2] as T

    value.splice(index1, 1, item2)
    value.splice(index2, 1, item1)

    return value
}

export function updateProp(prop: string, value: any) {
    return (object: any) => ({ ...object, [prop]: value })
}

export function isSafari(): boolean {
    if (typeof navigator === 'undefined') return false
    return navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')
}

export function isJobForPublicSectorWithRedirection(job: Job, org: Org): boolean {
    return Boolean(org.type === ORG_GOV_TYPE && job.externalLink)
}

// Function for slovak tilting (sklonovanie)
// Inputs:
// - word1: string which is used only for number 1
// - word24: string which is used for amount from 2 to 4
// - word5: string which is used for number 5 and more
export function amountTilting(value: number, word1: string, word24: string, word5: string) {
    value = Math.floor(value)
    if (value === 1) return word1
    else if (value >= 2 && value <= 4) return word24
    else return word5
}

export function moreOffersButtonText(number: number = 0, noOffersText: string = 'Všetky') {
    let nextString = ''

    if (number <= 0) {
        nextString = noOffersText
    } else if (number === 1) {
        nextString = '+ ' + number + ' ďalšia'
    } else if (number < 5) {
        nextString = '+ ' + number + ' ďalšie'
    } else {
        nextString = '+ ' + number + ' ďalších'
    }

    return nextString
}

export function seeOtherOffersButtonText(number: number = 0, textType: string, noOffersText: string = 'ďalšie') {
    let nextString = 'Pozri '

    switch (textType) {
        case 'projects':
            if (number <= 0) {
                nextString += noOffersText + ' projekty'
            } else if (number === 1) {
                nextString += 'ďalší projekt'
            } else if (number < 5) {
                nextString += 'ďalšíe ' + number + ' projekty'
            } else {
                nextString += 'ďalších ' + number + ' projektov'
            }
            break
        case 'mentors':
            if (number <= 0) {
                nextString += 'ďalších mentorov'
            } else if (number === 1) {
                nextString += 'ďalšieho mentora'
            } else {
                nextString += 'ďalších ' + number + ' mentorov'
            }
            break
        case 'jobs':
            if (number <= 0) {
                nextString += noOffersText + ' ponuky'
            } else if (number === 1) {
                nextString += 'ďalšiu ponuku'
            } else if (number < 5) {
                nextString += 'ďalšie ' + number + ' ponuky'
            } else {
                nextString += 'ďalších ' + number + ' ponúk'
            }
            break
        case 'volunteers':
            if (number <= 0) {
                nextString += 'ďalších dobrovoľníkov'
            } else if (number === 1) {
                nextString += 'ďalšieho dobrovoľníka'
            } else {
                nextString += 'ďalších ' + number + ' dobrovoľníkov'
            }
            break
        default:
            if (number <= 0) {
                nextString += noOffersText
            } else if (number === 1) {
                nextString += 'ďalšiu'
            } else if (number < 5) {
                nextString += 'ďalšie ' + number
            } else {
                nextString += 'ďalších ' + number
            }
            break
    }

    return nextString
}

export const processItemsInSeries = async <
    TItem = any,
    THandler extends (item: TItem) => Promise<any> = (item: any) => Promise<any>
>(
    items: TItem[],
    handler: THandler,
    batchSize: number = 1
): Promise<void> => {
    if (!items.length) {
        return
    }

    const thisBatchItems = items.slice(0, batchSize)
    const remainingItems = items.slice(batchSize)

    const promises = thisBatchItems.map(handler)

    await Promise.all(promises)

    if (remainingItems.length) {
        return processItemsInSeries(remainingItems, handler)
    }
}

export function parseResponseTime(seconds: number | undefined): string | null {
    if (!seconds) return null

    const minutes = Math.ceil(seconds / 60)
    if (minutes <= 60) return 'hodiny'

    const hours = Math.ceil(minutes / 60)
    if (hours === 1) return 'hodiny'
    if (hours > 1 && hours < 12) return `${hours} hodín`
    if (hours <= 24) return 'dňa'

    const days = Math.ceil(hours / 24)
    if (days === 1) return 'dňa'
    if (days > 1 && days < 7) return `${days} dní`

    const weeks = Math.ceil(days / 7)
    if (weeks === 1) return 'týždňa'
    return `${weeks} týždňov`
}

export function parseManuallyAddedResponseTime(
    responseTime: number | undefined,
    responseUnit: string | undefined
): string | undefined {
    if (!responseTime || !responseUnit) return undefined

    if (responseTime === 1) {
        if (responseUnit === 'hodín') return `hodiny`
        if (responseUnit === 'dní') return 'dňa'
        if (responseUnit === 'týždňov') return 'týždňa'
    }

    return `${responseTime} ${responseUnit}`
}

// Visibility
export function isPublishing(former: VisibilityType, actual: VisibilityType): boolean {
    return former !== actual && actual === VisibilityType.published
}

export function isUnpublishing(former: VisibilityType, actual: VisibilityType): boolean {
    return former !== actual && former === VisibilityType.published
}

export function isFinishing(former: VisibilityType, actual: VisibilityType): boolean {
    return former !== actual && actual === VisibilityType.hidden
}

export function isPublishStateChanged(former: VisibilityType, actual: VisibilityType): boolean {
    return isPublishing(former, actual) || isUnpublishing(former, actual)
}

// Feedback
export function isEligibleForUsersFeedback(
    action: FeedbackAction,
    userProfile: UserProfile,
    requestId?: string
): boolean {
    if (action === FeedbackAction.PROJECT_UNPUBLISH) {
        return !hasFeedbackGivenByUser(action, userProfile, {
            monthsBefore: 6
        })
    } else if (
        [
            FeedbackAction.JOB_APPLICATION,
            FeedbackAction.JOB_CREATE,
            FeedbackAction.JOB_UNPUBLISH,
            FeedbackAction.PROJECT_CREATE
        ].includes(action as any)
    ) {
        return !hasFeedbackGivenByUser(action, userProfile)
    } else if (
        [
            FeedbackAction.PROJECT_COOPERATION_ORG,
            FeedbackAction.PROJECT_COOPERATION_VOLUNTEER,
            FeedbackAction.MENTORING_FINISH_LOW_RATING,
            FeedbackAction.MENTORING_FINISH_HIGH_RATING
        ].includes(action as any)
    ) {
        return !hasFeedbackGivenByUser(action, userProfile, { requestId })
    } else {
        return true
    }
}

export function hasFeedbackGivenByUser(
    action: FeedbackAction,
    userProfile: UserProfile,
    params: { requestId?: string; monthsBefore?: number } = {}
) {
    return userProfile.feedbackMetas?.some(
        (value) =>
            value.action === action &&
            (!params.requestId || value.requestId === params.requestId) &&
            (!params.monthsBefore ||
                dayjs(value.createdAt as Date).isAfter(dayjs().subtract(params.monthsBefore!, 'months')))
    )
}

// Passives get the promoter wording unless a template spells out its own passive
// variant - matches how the Bloomreach mails only distinguish "not satisfied" (<= 6)
// from everyone else.
const NPS_BAND_FALLBACKS: Record<NpsBand, NpsBand[]> = {
    [NpsBand.DETRACTOR]: [NpsBand.DETRACTOR],
    [NpsBand.PASSIVE]: [NpsBand.PASSIVE, NpsBand.PROMOTER],
    [NpsBand.PROMOTER]: [NpsBand.PROMOTER, NpsBand.PASSIVE]
}

/**
 * Picks the question/placeholder variant matching the score the user just gave.
 * Variants are suffixed with the band they belong to, e.g.
 * `question1-detractor|question1-promoter`.
 */
export function pickTemplateByPrevAnswer(template: string, score?: number): string[] {
    const variants: string[] = template.split('|')
    let selectedVariant: string | undefined

    if (variants.length === 1) {
        selectedVariant = variants[0]
    } else {
        const band = score === undefined ? NpsBand.PROMOTER : getNpsBand(score)

        for (const candidate of NPS_BAND_FALLBACKS[band]) {
            selectedVariant = variants.find((key) => {
                const [, bandSuffix = ''] = key.split('-')
                return bandSuffix.split(':')[0] === candidate
            })
            if (selectedVariant) break
        }
    }

    if (!selectedVariant) {
        throw new Error(`Missing feedback template variant for NPS score "${score}"`)
    }

    const [key, param = ''] = selectedVariant.split(':')
    if (!key) {
        throw new Error(`Invalid feedback template variant "${selectedVariant}"`)
    }

    return [`feedbacks.form.${key}`, param]
}

export function countAllOpportunities(
    opportunities: { [p: string]: (Job | Project | Skill)[] },
    collection: Collections,
    orgId: OrgId
): { [p: string]: number } {
    return {
        [OrgOpportunitiesType.ACTIVE]: countOpportunities(
            opportunities,
            collection,
            orgId,
            OrgOpportunitiesType.ACTIVE
        ),
        [OrgOpportunitiesType.ALL]: countOpportunities(opportunities, collection, orgId, OrgOpportunitiesType.ALL)
    }
}

export function countOpportunities(
    opportunities: { [p: string]: (Job | Project | Skill)[] },
    collection: string,
    orgId: OrgId,
    type: OrgOpportunitiesType
): number {
    const items = opportunities[collection]
    if (!items) {
        return 0
    }

    return items.filter((item: Job | Skill | Project) => {
        if (type === OrgOpportunitiesType.ACTIVE) {
            return item.visibility === VisibilityType.published && item.orgId === orgId
        } else if (type === OrgOpportunitiesType.ALL) {
            return !item.deletedAt && item.orgId === orgId
        }
        return false
    }).length
}

export function updateConsent(agreement: Agreement, value: boolean, consents?: ConsentsData): ConsentsData {
    return {
        ...consents,
        [agreement.type as AgreementType]: {
            ...consents?.[agreement.type],
            ...(value ? { agreementId: agreement.id } : undefined),
            accepted: value
        }
    } as ConsentsData
}

export function normalizeEmailAddress(email: string): string {
    return email.toLowerCase().trim()
}

export function filterActivityApplications(applications: Activity[], filters: string[]) {
    if (!applications) {
        return []
    }

    if (!filters.length || filters.includes('all')) {
        return applications
    }

    return applications.filter((application) => filters.includes(application!.status! as string))
}

export function visibilityChange(former: VisibilityType, updated: VisibilityType) {
    if (former === updated) {
        return VisibilityChangeAction.NONE
    }
    if (former === VisibilityType.unlisted && updated === VisibilityType.hidden) {
        return VisibilityChangeAction.CHANGE
    }
    if (former === VisibilityType.draft && updated === VisibilityType.published) {
        return VisibilityChangeAction.PUBLISH
    }
    if ([VisibilityType.unlisted, VisibilityType.hidden].includes(former as any)) {
        return VisibilityChangeAction.PUBLISH
    }
    if (former === VisibilityType.published) {
        return VisibilityChangeAction.UNPUBLISH
    }
    return VisibilityChangeAction.CHANGE
}

interface LooseObject {
    [key: string]: any
}

export function shallowEqual(object1: LooseObject, object2: LooseObject) {
    const keys1 = Object.keys(object1)
    const keys2 = Object.keys(object2)

    if (keys1.length !== keys2.length) {
        return false
    }

    for (const key of keys1) {
        if (object1[key] !== object2[key]) {
            return false
        }
    }

    return true
}

export function isJobApplicationDraft(obj: any): obj is JobApplicationFormDraft {
    return typeof obj === 'object' && obj !== null && typeof obj.jobId === 'string' && typeof obj.orgId === 'string'
}

export function isJob(obj: any): obj is Job {
    return typeof obj === 'object' && obj !== null && typeof obj.id === 'string'
}

export function getNotificationKey(base: NotificationType, id?: string): string {
    return [base, id].filter(Boolean).join('_')
}

export function parseNotificationKey(topic: string): {
    objectName: NotificationType
    objectId: string
} {
    const [objectName = '', objectId = ''] = (topic || '').split('_')

    return {
        objectName: objectName as NotificationType,
        objectId
    }
}
